import Text from '@/components/react-native/text';
import TextInput from '@/components/react-native/text-input';
import TouchableOpacity from '@/components/react-native/touchable-opacity';
import View from '@/components/react-native/view';
import {ENoteCategory, ENoteTag, INoteInfo, IReference} from '@/types/airport';
import classNames from 'classnames';
import React, {useState} from 'react';

const CategoryLabel: Record<ENoteCategory, string> = {
  [ENoteCategory.CAUTION]: 'Caution',
  [ENoteCategory.RESTRICTIONS]: 'Restrictions',
  [ENoteCategory.ARRIVAL]: 'Arrival',
  [ENoteCategory.DEPARTURE]: 'Departure',
  [ENoteCategory.GROUND_OPERATIONS]: 'Ground Operations',
  [ENoteCategory.FLEET_INFORMATION]: 'Fleet Information'
};

const TagLabel: Record<ENoteTag, string> = {
  [ENoteTag.ARRIVAL]: 'Arrival',
  [ENoteTag.DEPARTURE]: 'Departure',
  [ENoteTag.GROUND_OPERATIONS]: 'Ground Operations'
};

// Optimized UI components with React.memo
export const CategoryTag = React.memo(
  ({category}: {category: ENoteCategory}) =>
    CategoryLabel[category] && (
      <View className="bg-[#C4B5FD] px-[8px] py-[4px] rounded-[16px] whitespace-nowrap">
        <Text
          fontFamily="Geist"
          className="text-[#1C1917] text-[14px] font-[500] tracking-[-0.28px]"
        >
          {CategoryLabel[category]}
        </Text>
      </View>
    )
);
CategoryTag.displayName = 'CategoryTag';

// Optimized UI components with React.memo
export const NoteTag = React.memo(
  ({tag}: {tag: ENoteTag}) =>
    TagLabel[tag] && (
      <View className="bg-[#C4B5FD] px-[8px] py-[4px] rounded-[16px] whitespace-nowrap">
        <Text
          fontFamily="Geist"
          className="text-[#1C1917] text-[14px] font-[500] tracking-[-0.28px]"
        >
          {TagLabel[tag]}
        </Text>
      </View>
    )
);
NoteTag.displayName = 'NoteTag';

export const WarningItem = React.memo(
  ({
    note,
    category,
    content,
    onPress
  }: {
    note: INoteInfo;
    category: ENoteCategory;
    content: string;
    onPress?: (note: INoteInfo) => void;
  }) => (
    <TouchableOpacity
      className="py-[16px] px-[16px] flex-row items-center"
      onClick={() => onPress?.(note)}
      activeOpacity={0.7}
    >
      <CategoryTag category={category} />
      <View className="flex-grow w-[90%]">
        <Text
          fontFamily="Geist"
          className="ml-[8px] text-[#FAFAF9] text-[16px] font-[500] tracking-[-0.32px] leading-[24px]"
        >
          {content}
        </Text>
      </View>
    </TouchableOpacity>
  )
);

WarningItem.displayName = 'WarningItem';

export const EditableReferencesTable = React.memo(
  ({
    references,
    isEditing,
    onReferencesChange,
    onEditToggle,
    onSave,
    onCancel,
    preview,
    showHeader = true
  }: {
    references: IReference[];
    isEditing: boolean;
    onReferencesChange: (references: IReference[]) => void;
    onEditToggle: () => void;
    onSave: () => void;
    onCancel: () => void;
    preview?: boolean;
    showHeader?: boolean;
  }) => {
    const [localReferences, setLocalReferences] =
      useState<IReference[]>(references);

    React.useEffect(() => {
      setLocalReferences(references);
    }, [references]);

    const handleReferenceChange = (
      index: number,
      field: 'header' | 'content',
      value: string
    ) => {
      const newReferences = [...localReferences];
      newReferences[index] = {...newReferences[index], [field]: value};
      setLocalReferences(newReferences);
      onReferencesChange(newReferences);
    };

    const handleAddReference = () => {
      const newReferences = [...localReferences, {header: '', content: ''}];
      setLocalReferences(newReferences);
      onReferencesChange(newReferences);
    };

    const handleRemoveReference = (index: number) => {
      const newReferences = localReferences.filter((_, i) => i !== index);
      setLocalReferences(newReferences);
      onReferencesChange(newReferences);
    };

    if (isEditing) {
      return (
        <View className="py-[8px]">
          {/* Edit Mode Header */}
          <View className="flex-row items-center justify-between mb-[16px]">
            <Text
              fontFamily="GeistMono"
              className="text-[#E7E5E4] text-[14px] font-[500] tracking-[-0.28px] uppercase"
            >
              EDITING REFERENCES
            </Text>
            <View className="flex-row gap-[8px]">
              <TouchableOpacity
                className="px-[12px] py-[6px] bg-[#38BDF8] rounded-[4px]"
                onClick={onSave}
              >
                <Text
                  fontFamily="Geist"
                  className="text-[#1C1917] text-[14px] font-[500]"
                >
                  Save
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                className="px-[12px] py-[6px] rounded-[4px]"
                onClick={onCancel}
              >
                <Text
                  fontFamily="Geist"
                  className="text-[#38BDF8] text-[14px] font-[500]"
                >
                  Cancel
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Editable References */}
          {localReferences.map((item, index) => (
            <View key={index} className="flex-row mb-[8px] relative">
              {showHeader && (
                <View className="w-[240px] bg-[rgba(112,112,112,0.3)] border-l border-t border-r border-b border-[#707070] px-[16px] py-[12px]">
                  <TextInput
                    value={item.header}
                    onChangeText={(value: any) =>
                      handleReferenceChange(index, 'header', value)
                    }
                    multiline
                    className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] bg-transparent border-none outline-none font-mono"
                    placeholder="Header"
                    placeholderTextColor="#78716C"
                  />
                </View>
              )}
              <View
                className={classNames(
                  'flex-1 border-t border-r border-b border-[#707070] px-[16px] py-[12px]',
                  {
                    'border-l': !showHeader
                  }
                )}
              >
                <TextInput
                  value={item.content}
                  onChangeText={(value: any) =>
                    handleReferenceChange(index, 'content', value)
                  }
                  multiline
                  className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] bg-transparent border-none outline-none font-mono"
                  placeholder="Content"
                  placeholderTextColor="#78716C"
                />
              </View>
              <TouchableOpacity
                className="absolute -right-[8px] -top-[8px] w-[24px] h-[24px] bg-[#EF4444] rounded-full flex items-center justify-center"
                onClick={() => handleRemoveReference(index)}
              >
                <Text className="text-white text-[14px] font-bold leading-none">
                  ×
                </Text>
              </TouchableOpacity>
            </View>
          ))}

          {/* Add Reference Button */}
          <TouchableOpacity
            className="flex-row items-center gap-[8px] px-[16px] py-[12px] bg-[rgba(112,112,112,0.2)] border border-dashed border-[#707070] rounded-[4px] mt-[8px]"
            onClick={handleAddReference}
          >
            <Text className="text-[#38BDF8] text-[20px] font-bold leading-none">
              +
            </Text>
            <Text
              fontFamily="Geist"
              className="text-[#38BDF8] text-[16px] font-[500] tracking-[-0.32px]"
            >
              Add Reference
            </Text>
          </TouchableOpacity>
        </View>
      );
    }

    // View Mode
    return (
      <View className="py-[8px]">
        <View className="flex-row items-center justify-between mb-[16px]">
          <View className="flex-1" />
          {!preview && localReferences.length > 0 && (
            <TouchableOpacity
              className="px-[12px] py-[6px] bg-[#38BDF8] rounded-[4px]"
              onClick={onEditToggle}
            >
              <Text
                fontFamily="Geist"
                className="text-[#1C1917] text-[14px] font-[500]"
              >
                Edit
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {localReferences.map((item, index) => (
          <View className="flex-row" key={index}>
            {showHeader && (
              <View className="w-[240px] bg-[rgba(112,112,112,0.3)] border-l border-t border-r border-b border-[#707070] px-[16px] py-[16px]">
                <Text
                  fontFamily="GeistMono"
                  className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px]"
                >
                  {item.header}
                </Text>
              </View>
            )}
            <View
              className={classNames(
                'flex-1 border-t border-r border-b border-[#707070] px-[16px] py-[16px]',
                {
                  'border-l': !showHeader
                }
              )}
            >
              <Text
                fontFamily="GeistMono"
                className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px]"
              >
                {item.content}
              </Text>
            </View>
          </View>
        ))}
        {localReferences.length === 0 && (
          <TouchableOpacity
            className="flex-row items-center gap-[8px] px-[16px] py-[12px] bg-[rgba(112,112,112,0.2)] border border-dashed border-[#707070] rounded-[4px] mt-[8px]"
            onClick={handleAddReference}
          >
            <Text className="text-[#38BDF8] text-[20px] font-bold leading-none">
              +
            </Text>
            <Text
              fontFamily="Geist"
              className="text-[#38BDF8] text-[16px] font-[500] tracking-[-0.32px]"
            >
              Add Reference
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }
);
EditableReferencesTable.displayName = 'EditableReferencesTable';

export const ReferencesTable = React.memo(
  ({references, editable}: {references: IReference[]; editable: boolean}) => (
    <View className="py-[8px]">
      {references.map((item, index) => (
        <View className="flex-row" key={index}>
          <View className="w-[240px] bg-[rgba(112,112,112,0.3)] border-l border-t border-r border-b border-[#707070] px-[16px] py-[16px]">
            <Text
              fontFamily="GeistMono"
              className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] uppercase"
            >
              {item.header}
            </Text>
          </View>
          <View className="flex-1 border-t border-r border-b border-[#707070] px-[16px] py-[16px]">
            <Text
              fontFamily="GeistMono"
              className="text-[#E7E5E4] text-[16px] font-[500] tracking-[-0.32px] uppercase"
            >
              {item.content}
            </Text>
          </View>
        </View>
      ))}
    </View>
  )
);
ReferencesTable.displayName = 'ReferencesTable';

export const EmptyState = React.memo(() => (
  <View className="flex-1 items-center justify-center bg-[#010009] px-[24px]">
    <Text
      fontFamily="Geist"
      className="text-[#78716C] text-[18px] font-[500] tracking-[-0.36px] text-center"
    >
      Airport information is not available
    </Text>
  </View>
));

EmptyState.displayName = 'EmptyState';

export const WarningTag = React.memo(() => (
  <View className="bg-[#E94860] px-[8px] py-[4px] rounded-[16px]">
    <Text
      fontFamily="Geist"
      className="text-[#1C1917] text-[14px] font-[500] tracking-[-0.28px]"
    >
      Warning
    </Text>
  </View>
));

WarningTag.displayName = 'WarningTag';

export const CautionTag = React.memo(() => (
  <View className="bg-[#EB8200] px-[8px] py-[4px] rounded-[16px]">
    <Text
      fontFamily="Geist"
      className="text-[#1C1917] text-[14px] font-[500] tracking-[-0.28px]"
    >
      Caution
    </Text>
  </View>
));

CautionTag.displayName = 'CautionTag';
